import { teamCountryToPlacesCode } from '@/constants/teamCountries';
import { loadGoogleMaps } from '@/lib/loadGoogleMaps';
import { parseAddressComponents, type ParsedPlaceAddress } from '@/lib/parseGooglePlaceAddress';

/** Geocode a free-text address, restricted to the team's registered country. */
export async function geocodeServiceAddress(
  address: string,
  country?: string | null,
): Promise<ParsedPlaceAddress> {
  const query = address.trim();
  if (!query) {
    throw new Error('Address is empty.');
  }

  const countryCode = teamCountryToPlacesCode(country);
  const google = await loadGoogleMaps(countryCode);
  const geocoder = new google.maps.Geocoder();

  return new Promise((resolve, reject) => {
    geocoder.geocode(
      {
        address: query,
        region: countryCode,
        componentRestrictions: { country: countryCode },
      },
      (results, status) => {
        if (status === 'ZERO_RESULTS' || !results?.[0]) {
          reject(new Error("Could not verify this address in your team's registered country."));
          return;
        }
        if (status !== 'OK') {
          reject(new Error('Could not verify this address with Google Maps.'));
          return;
        }

        const result = results[0];
        const resultCountry = result.address_components
          ?.find((component) => component.types.includes('country'))
          ?.short_name?.trim()
          .toLowerCase();
        if (resultCountry && resultCountry !== countryCode) {
          reject(new Error("This address is outside your team's registered country."));
          return;
        }

        resolve(
          parseAddressComponents(
            result.formatted_address?.trim() ?? query,
            result.address_components ?? [],
          ),
        );
      },
    );
  });
}
